import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Users,
  Phone,
  Mail,
  Clock,
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  PieChart,
  Filter,
  Search,
  Download,
  Upload,
  Settings,
  Eye,
  Edit,
  Trash2,
  Plus,
  RefreshCw,
  Calendar,
  FileText,
  Database,
  Zap,
  Target,
  Globe,
  Lock,
  Unlock,
  UserCheck,
  UserX,
  PhoneOff,
  MailX,
  AlertCircle,
  Info,
  Star,
  Bookmark,
  Archive,
  Bell,
  ChevronDown,
  ChevronRight,
  MoreVertical,
  ExternalLink,
  Copy,
  Share,
  Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { cn } from '../../utils/cn';

// Types for Compliance Dashboard
interface ComplianceStats {
  tcpa: {
    totalChecks: number;
    passedChecks: number;
    failedChecks: number;
    complianceRate: number;
    commonViolations: Array<{ violation: string; count: number }>;
    avgResponseTime: number;
    totalCost: number;
  };
  optOut: {
    totalOptOuts: number;
    optOutsByType: Record<string, number>;
    optOutsByMethod: Record<string, number>;
    globalOptOuts: number;
    reinstatedOptOuts: number;
    optOutRate: number;
    avgProcessingTime: number;
  };
  consent: {
    totalConsents: number;
    activeConsents: number;
    expiredConsents: number;
    revokedConsents: number;
    pendingVerification: number;
    consentsByType: Record<string, number>;
    consentsByChannel: Record<string, number>;
    tcpaCompliantRate: number;
    avgConsentDuration: number;
  };
  summary: {
    totalChecks: number;
    complianceRate: number;
    totalOptOuts: number;
    totalConsents: number;
    activeConsents: number;
  };
}

interface ConsentRecord {
  id: string;
  contactPhone: string;
  contactEmail?: string;
  contactName?: string;
  consentType: string;
  consentMethod: string;
  consentChannel: string;
  consentStatus: string;
  consentTimestamp: string;
  expiresAt?: string;
  tcpaCompliant: boolean;
  verificationStatus: string;
}

interface OptOutRecord {
  id: string;
  contactPhone?: string;
  contactEmail?: string;
  optOutType: string;
  optOutMethod: string;
  optOutTimestamp: string;
  globalOptOut: boolean;
  processed: boolean;
  reinstated: boolean;
}

interface ComplianceCheck {
  id: string;
  checkType: string;
  contactPhone?: string;
  checkTimestamp: string;
  checkResult: string;
  isCompliant: boolean;
  violations?: string[];
  warnings?: string[];
  cost: number;
  responseTimeMs: number;
}

export const ComplianceDashboard: React.FC = () => {
  const [stats, setStats] = useState<ComplianceStats | null>(null);
  const [consentRecords, setConsentRecords] = useState<ConsentRecord[]>([]);
  const [optOutRecords, setOptOutRecords] = useState<OptOutRecord[]>([]);
  const [complianceChecks, setComplianceChecks] = useState<ComplianceCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'consent' | 'opt-out' | 'checks' | 'settings'>('overview');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState(false);

  // Fetch compliance data
  const fetchComplianceData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);

      const [statsRes, consentRes, optOutRes, checksRes] = await Promise.all([
        fetch(`/api/compliance/dashboard/overview?${params}`),
        fetch(`/api/compliance/records/consent?limit=10&${params}`),
        fetch(`/api/compliance/records/opt-out?limit=10&${params}`),
        fetch(`/api/compliance/records/compliance-checks?limit=10&${params}`)
      ]);

      const [statsData, consentData, optOutData, checksData] = await Promise.all([
        statsRes.json(),
        consentRes.json(),
        optOutRes.json(),
        checksRes.json()
      ]);

      setStats(statsData);
      setConsentRecords(consentData.records || []);
      setOptOutRecords(optOutData.records || []);
      setComplianceChecks(checksData.records || []);
    } catch (error) {
      console.error('Failed to fetch compliance data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchComplianceData();
  }, [fetchComplianceData]);

  const StatCard = ({ 
    title, 
    value, 
    change, 
    icon: Icon, 
    color = 'blue',
    subtitle,
    trend
  }: {
    title: string;
    value: string | number;
    change?: string;
    icon: React.ElementType;
    color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple';
    subtitle?: string;
    trend?: 'up' | 'down' | 'neutral';
  }) => {
    const colorClasses = {
      blue: 'from-blue-500/20 to-cyan-500/20 border-blue-500/30',
      green: 'from-green-500/20 to-emerald-500/20 border-green-500/30',
      red: 'from-red-500/20 to-pink-500/20 border-red-500/30',
      yellow: 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30',
      purple: 'from-purple-500/20 to-indigo-500/20 border-purple-500/30'
    };

    const iconColorClasses = {
      blue: 'text-blue-400',
      green: 'text-green-400',
      red: 'text-red-400',
      yellow: 'text-yellow-400',
      purple: 'text-purple-400'
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "relative overflow-hidden rounded-xl border backdrop-blur-sm",
          "bg-gradient-to-br p-6 transition-all duration-300 hover:scale-105",
          colorClasses[color]
        )}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-neutral-400">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
            {subtitle && (
              <p className="text-xs text-neutral-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={cn("p-3 rounded-lg bg-black/20", iconColorClasses[color])}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
        
        {change && (
          <div className="flex items-center mt-4 space-x-2">
            {trend === 'up' && <TrendingUp className="h-4 w-4 text-green-400" />}
            {trend === 'down' && <TrendingDown className="h-4 w-4 text-red-400" />}
            {trend === 'neutral' && <Activity className="h-4 w-4 text-neutral-400" />}
            <span className={cn(
              "text-sm font-medium",
              trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-neutral-400'
            )}>
              {change}
            </span>
          </div>
        )}
      </motion.div>
    );
  };

  const TabButton = ({ 
    id, 
    label, 
    icon: Icon, 
    isActive, 
    onClick 
  }: {
    id: string;
    label: string;
    icon: React.ElementType;
    isActive: boolean;
    onClick: () => void;
  }) => (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        "flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200",
        isActive
          ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white border border-blue-500/30"
          : "text-neutral-400 hover:text-white hover:bg-white/5"
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </motion.button>
  );

  const ComplianceOverview = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Compliance Rate"
          value={`${stats?.summary.complianceRate.toFixed(1)}%`}
          change="+2.3% from last month"
          icon={Shield}
          color="green"
          trend="up"
        />
        <StatCard
          title="Total Checks"
          value={stats?.summary.totalChecks.toLocaleString() || '0'}
          change="+156 today"
          icon={Activity}
          color="blue"
          trend="up"
        />
        <StatCard
          title="Active Consents"
          value={stats?.summary.activeConsents.toLocaleString() || '0'}
          change="98.2% valid"
          icon={UserCheck}
          color="purple"
          trend="neutral"
        />
        <StatCard
          title="Opt-Outs"
          value={stats?.summary.totalOptOuts.toLocaleString() || '0'}
          change="-5.1% this week"
          icon={UserX}
          color="red"
          trend="down"
        />
      </div>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Trends */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 rounded-xl border border-neutral-800 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Compliance Trends</h3>
            <Button variant="ghost" size="sm">
              <BarChart3 className="h-4 w-4" />
            </Button>
          </div>
          <div className="h-64 flex items-center justify-center text-neutral-500">
            <div className="text-center">
              <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Chart visualization would go here</p>
            </div>
          </div>
        </motion.div>

        {/* Violation Breakdown */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 rounded-xl border border-neutral-800 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Common Violations</h3>
            <Button variant="ghost" size="sm">
              <PieChart className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-3">
            {stats?.tcpa.commonViolations.slice(0, 5).map((violation, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-neutral-300 truncate flex-1">
                  {violation.violation}
                </span>
                <span className="text-sm font-medium text-white ml-2">
                  {violation.count}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 rounded-xl border border-neutral-800 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Recent Compliance Checks</h3>
          <Button variant="ghost" size="sm">
            <Eye className="h-4 w-4" />
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Contact</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Type</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Result</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Time</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Cost</th>
              </tr>
            </thead>
            <tbody>
              {complianceChecks.slice(0, 5).map((check) => (
                <tr key={check.id} className="border-b border-neutral-800/50">
                  <td className="py-3 text-sm text-white">
                    {check.contactPhone || 'N/A'}
                  </td>
                  <td className="py-3 text-sm text-neutral-300">
                    {check.checkType.replace('_', ' ').toUpperCase()}
                  </td>
                  <td className="py-3">
                    <span className={cn(
                      "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                      check.isCompliant
                        ? "bg-green-500/20 text-green-400"
                        : "bg-red-500/20 text-red-400"
                    )}>
                      {check.isCompliant ? (
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                      ) : (
                        <XCircle className="h-3 w-3 mr-1" />
                      )}
                      {check.checkResult.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 text-sm text-neutral-300">
                    {check.responseTimeMs}ms
                  </td>
                  <td className="py-3 text-sm text-neutral-300">
                    ${check.cost.toFixed(4)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );

  const ConsentManagement = () => (
    <div className="space-y-6">
      {/* Consent Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Consents"
          value={stats?.consent.totalConsents.toLocaleString() || '0'}
          icon={UserCheck}
          color="blue"
        />
        <StatCard
          title="TCPA Compliant"
          value={`${stats?.consent.tcpaCompliantRate.toFixed(1)}%`}
          icon={Shield}
          color="green"
        />
        <StatCard
          title="Pending Verification"
          value={stats?.consent.pendingVerification.toLocaleString() || '0'}
          icon={Clock}
          color="yellow"
        />
      </div>

      {/* Consent Records Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 rounded-xl border border-neutral-800 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Consent Records</h3>
          <div className="flex space-x-2">
            <Button variant="ghost" size="sm">
              <Upload className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm">
              <Download className="h-4 w-4" />
            </Button>
            <Button size="sm" className="bg-gradient-to-r from-blue-500 to-purple-500">
              <Plus className="h-4 w-4 mr-2" />
              Add Consent
            </Button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Contact</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Type</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Channel</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Status</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Date</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {consentRecords.map((record) => (
                <tr key={record.id} className="border-b border-neutral-800/50 hover:bg-white/5">
                  <td className="py-3">
                    <div>
                      <p className="text-sm font-medium text-white">{record.contactPhone}</p>
                      {record.contactEmail && (
                        <p className="text-xs text-neutral-400">{record.contactEmail}</p>
                      )}
                    </div>
                  </td>
                  <td className="py-3 text-sm text-neutral-300">
                    {record.consentType.replace('_', ' ').toUpperCase()}
                  </td>
                  <td className="py-3 text-sm text-neutral-300">
                    {record.consentChannel.toUpperCase()}
                  </td>
                  <td className="py-3">
                    <span className={cn(
                      "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                      record.consentStatus === 'active'
                        ? "bg-green-500/20 text-green-400"
                        : record.consentStatus === 'pending'
                        ? "bg-yellow-500/20 text-yellow-400"
                        : "bg-red-500/20 text-red-400"
                    )}>
                      {record.consentStatus.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 text-sm text-neutral-300">
                    {new Date(record.consentTimestamp).toLocaleDateString()}
                  </td>
                  <td className="py-3">
                    <div className="flex space-x-2">
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );

  const OptOutManagement = () => (
    <div className="space-y-6">
      {/* Opt-Out Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Opt-Outs"
          value={stats?.optOut.totalOptOuts.toLocaleString() || '0'}
          icon={UserX}
          color="red"
        />
        <StatCard
          title="Global Opt-Outs"
          value={stats?.optOut.globalOptOuts.toLocaleString() || '0'}
          icon={Globe}
          color="purple"
        />
        <StatCard
          title="Reinstated"
          value={stats?.optOut.reinstatedOptOuts.toLocaleString() || '0'}
          icon={RefreshCw}
          color="green"
        />
      </div>

      {/* Opt-Out Records Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 rounded-xl border border-neutral-800 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Opt-Out Records</h3>
          <div className="flex space-x-2">
            <Button variant="ghost" size="sm">
              <Upload className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm">
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Contact</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Type</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Method</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Status</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Date</th>
                <th className="text-left text-sm font-medium text-neutral-400 pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {optOutRecords.map((record) => (
                <tr key={record.id} className="border-b border-neutral-800/50 hover:bg-white/5">
                  <td className="py-3">
                    <div>
                      <p className="text-sm font-medium text-white">
                        {record.contactPhone || record.contactEmail || 'N/A'}
                      </p>
                    </div>
                  </td>
                  <td className="py-3 text-sm text-neutral-300">
                    {record.optOutType.toUpperCase()}
                  </td>
                  <td className="py-3 text-sm text-neutral-300">
                    {record.optOutMethod.replace('_', ' ').toUpperCase()}
                  </td>
                  <td className="py-3">
                    <div className="flex items-center space-x-2">
                      <span className={cn(
                        "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                        record.processed
                          ? "bg-green-500/20 text-green-400"
                          : "bg-yellow-500/20 text-yellow-400"
                      )}>
                        {record.processed ? 'PROCESSED' : 'PENDING'}
                      </span>
                      {record.globalOptOut && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                          GLOBAL
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 text-sm text-neutral-300">
                    {new Date(record.optOutTimestamp).toLocaleDateString()}
                  </td>
                  <td className="py-3">
                    <div className="flex space-x-2">
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {!record.reinstated && (
                        <Button variant="ghost" size="sm">
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-neutral-400">Loading compliance dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Compliance Dashboard
              </h1>
              <p className="text-neutral-400 mt-2">
                Monitor TCPA compliance, consent management, and opt-out tracking
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white"
                />
                <span className="text-neutral-400">to</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <Button
                onClick={fetchComplianceData}
                className="bg-gradient-to-r from-blue-500 to-purple-500"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex space-x-2 mb-8 bg-neutral-900/50 backdrop-blur-sm rounded-xl p-2 border border-neutral-800"
        >
          <TabButton
            id="overview"
            label="Overview"
            icon={BarChart3}
            isActive={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
          />
          <TabButton
            id="consent"
            label="Consent Management"
            icon={UserCheck}
            isActive={activeTab === 'consent'}
            onClick={() => setActiveTab('consent')}
          />
          <TabButton
            id="opt-out"
            label="Opt-Out Management"
            icon={UserX}
            isActive={activeTab === 'opt-out'}
            onClick={() => setActiveTab('opt-out')}
          />
          <TabButton
            id="checks"
            label="Compliance Checks"
            icon={Shield}
            isActive={activeTab === 'checks'}
            onClick={() => setActiveTab('checks')}
          />
          <TabButton
            id="settings"
            label="Settings"
            icon={Settings}
            isActive={activeTab === 'settings'}
            onClick={() => setActiveTab('settings')}
          />
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'overview' && <ComplianceOverview />}
            {activeTab === 'consent' && <ConsentManagement />}
            {activeTab === 'opt-out' && <OptOutManagement />}
            {activeTab === 'checks' && (
              <div className="text-center py-12">
                <Shield className="h-12 w-12 text-neutral-500 mx-auto mb-4" />
                <p className="text-neutral-400">Compliance checks view coming soon...</p>
              </div>
            )}
            {activeTab === 'settings' && (
              <div className="text-center py-12">
                <Settings className="h-12 w-12 text-neutral-500 mx-auto mb-4" />
                <p className="text-neutral-400">Settings view coming soon...</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}; 